/**
 * AdminLandingPage - /admin section index (Phase 12.1).
 *
 * 3-column grid (md+) of AdminSectionCards covering every admin surface
 * listed in SPEC §9 AdminLandingPage. Routing to API keys (Developers
 * tab) and to the AdminWebhooksPage (owned by Phase 9 frontend agent)
 * uses path strings only - no imports of those components - so this
 * file stays compilable regardless of what the parallel agent ships.
 */
import { AdminSectionCard } from "@/components/admin/AdminSectionCard";
import { usePageTitle } from "@/hooks/usePageTitle";

interface AdminSection {
  title: string;
  description: string;
  icon: string;
  to: string;
}

const SECTIONS: AdminSection[] = [
  {
    title: "Users",
    description: "View, invite, and manage accounts across all companies.",
    icon: "fa-duotone fa-thin fa-users",
    to: "/admin/users",
  },
  {
    title: "Settings",
    description:
      "Edit system_settings rows and navigate to per-component admin pages.",
    icon: "fa-duotone fa-thin fa-gear",
    to: "/admin/settings",
  },
  {
    title: "API keys",
    description:
      "Issue and revoke keys for the public REST API and AI agents.",
    icon: "fa-duotone fa-thin fa-key",
    to: "/settings?tab=developers",
  },
  {
    title: "Webhooks",
    description: "Register outbound webhook endpoints and replay deliveries.",
    icon: "fa-duotone fa-thin fa-bolt",
    to: "/admin/webhooks",
  },
  {
    title: "Logs",
    description:
      "Live tail of system_log with filters by service, action, level, and trace id.",
    icon: "fa-duotone fa-thin fa-file-lines",
    to: "/admin/logs",
  },
  {
    title: "Audit",
    description:
      "Cross-ticket audit search over ticket_events for investigations.",
    icon: "fa-duotone fa-thin fa-magnifying-glass",
    to: "/admin/audit",
  },
  {
    title: "Security",
    description:
      "Failed logins, active sessions, and most-targeted accounts.",
    icon: "fa-duotone fa-thin fa-shield-keyhole",
    to: "/admin/security",
  },
  {
    title: "Import status",
    description:
      "Freshdesk legacy, Gemini archive, inbound email queue, and Buyist API sync.",
    icon: "fa-duotone fa-thin fa-cloud-arrow-down",
    to: "/admin/import-status",
  },
  {
    title: "Health",
    description:
      "Per-component reachability for Postgres, Redis, Microsoft 365 mail, Spaces, and Buyist API.",
    icon: "fa-duotone fa-thin fa-heart-pulse",
    to: "/admin/health",
  },
  {
    title: "Spam blocklist",
    description: "Sender blocklist viewer with add and unblock actions.",
    icon: "fa-duotone fa-thin fa-shield-xmark",
    to: "/admin/spam",
  },
  {
    title: "Sender rewrites",
    description:
      "Reroute inbound From: addresses (e.g. operator@ to datacontrol@) before ticket creation.",
    icon: "fa-duotone fa-thin fa-arrow-right-arrow-left",
    to: "/admin/sender-rewrites",
  },
];

export function AdminLandingPage(): JSX.Element {
  usePageTitle("System Admin");
  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">
          System Admin
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Operate and inspect Buyist Support. Each section is gated to
          system administrators.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {SECTIONS.map((section) => (
          <AdminSectionCard
            key={section.to}
            title={section.title}
            description={section.description}
            icon={section.icon}
            to={section.to}
          />
        ))}
      </div>
    </div>
  );
}

export default AdminLandingPage;
